// Lightweight trade salary-match legality — the same rules summary.ts warns about (soft-cap
// take-back % including the second-apron 100% tier, hard-cap no-increase), packaged as O(1)
// arithmetic so trade search loops (makeItWork's legalization pass) can check legality per
// candidate asset without the heavy summary() call. summary.ts drives its warning off
// evaluateSalaryMatch so the two implementations can't drift.
//
// All amounts are in THOUSANDS, matching contract.amount / team.getPayroll / aprons.ts.
// (summary.ts displays in $M; every comparison here is scale-invariant.)

import { g } from "../../util/index.ts";
import { idb } from "../../db/index.ts";
import { team } from "../index.ts";
import isUntradable from "./isUntradable.ts";
import { tradeSalaryMatchPct } from "../finances/aprons.ts";
import type { TradeTeams } from "../../../common/types.ts";

export type SalaryMatchContext = {
	capType: "soft" | "hard" | "none";
	cap: number;
	payrolls: [number, number]; // per trade side, BEFORE the trade
	matchPcts: [number, number];
};

export const buildSalaryMatchContext = (
	payrolls: [number, number],
): SalaryMatchContext => ({
	capType: g.get("salaryCapType"),
	cap: g.get("salaryCap"),
	payrolls,
	matchPcts: [
		tradeSalaryMatchPct(payrolls[0]),
		tradeSalaryMatchPct(payrolls[1]),
	],
});

// Payrolls don't change during a trade search, so build this once per makeItWork call.
export const createSalaryMatchContext = async (
	tids: [number, number],
): Promise<SalaryMatchContext> =>
	buildSalaryMatchContext([
		await team.getPayroll(tids[0]),
		await team.getPayroll(tids[1]),
	]);

export type SalaryMatchResult = {
	legal: boolean;
	// Per-team violation flags (summary picks the first violating side for its warning text).
	violations: [boolean, boolean];
	// The side to fix first: the larger-excess violator. undefined when legal.
	violator: 0 | 1 | undefined;
	ratios: [number, number];
	overCap: [boolean, boolean];
	payrollsAfter: [number, number];
	// Monotone dollar overage per team, 0 when compliant. Greedy-sort heuristic only —
	// `legal` is the exact (floor-based) check.
	excesses: [number, number];
};

// salariesOut = total outgoing in-trade salary per side, thousands. O(1).
export const evaluateSalaryMatch = (
	ctx: SalaryMatchContext,
	salariesOut: [number, number],
): SalaryMatchResult => {
	const result: SalaryMatchResult = {
		legal: true,
		violations: [false, false],
		violator: undefined,
		ratios: [100, 100],
		overCap: [false, false],
		payrollsAfter: [ctx.payrolls[0], ctx.payrolls[1]],
		excesses: [0, 0],
	};

	if (ctx.capType === "none") {
		return result;
	}

	for (const j of [0, 1] as const) {
		const k = j === 0 ? 1 : 0;
		const out = salariesOut[j];
		const incoming = salariesOut[k];

		result.ratios[j] =
			out > 0
				? Math.floor((100 * incoming) / out)
				: incoming > 0
					? Infinity
					: 100;
		result.payrollsAfter[j] = ctx.payrolls[j] + incoming - out;
		result.overCap[j] = result.payrollsAfter[j] > ctx.cap;

		if (ctx.capType === "soft") {
			if (result.overCap[j] && result.ratios[j] > ctx.matchPcts[j]) {
				result.violations[j] = true;
				result.excesses[j] = Math.max(
					0,
					incoming - (out * ctx.matchPcts[j]) / 100,
				);
			}
		} else {
			// Hard cap: an over-cap team can't increase its payroll
			if (result.overCap[j] && incoming > out) {
				result.violations[j] = true;
				result.excesses[j] = Math.min(
					incoming - out,
					result.payrollsAfter[j] - ctx.cap,
				);
			}
		}
	}

	result.legal = !result.violations[0] && !result.violations[1];
	if (!result.legal) {
		result.violator = result.excesses[1] > result.excesses[0] ? 1 : 0;
	}

	return result;
};

// Total outgoing in-trade salary per side, mirroring summary.ts's filter (in-trade pids,
// skipping untradable players). Callers maintain it incrementally afterward.
export const getTradeSalariesOut = async (
	teams: TradeTeams,
): Promise<[number, number]> => {
	const salariesOut: [number, number] = [0, 0];
	for (const i of [0, 1] as const) {
		const players = await idb.cache.players.indexGetAll(
			"playersByTid",
			teams[i].tid,
		);
		for (const p of players) {
			if (teams[i].pids.includes(p.pid) && !isUntradable(p).untradable) {
				salariesOut[i] += p.contract.amount;
			}
		}
	}
	return salariesOut;
};
